import React, { useMemo, useState } from 'react';

type Column = {
  field?: string;
  headerText?: string;
  template?: React.ComponentType<any> | ((props: any) => React.ReactNode);
  width?: string | number;
  textAlign?: string;
  type?: string;
};

interface SimpleTableProps {
  columns: Column[];
  data: any[];
  pageSize?: number;
  selectable?: boolean;
  onDelete?: (selected: any[]) => void;
  onRowClick?: (row: any) => void;
}

export const SimpleTable = ({ columns, data, pageSize = 10, selectable = false, onDelete, onRowClick }: SimpleTableProps) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc'|'desc'} | null>(null);

  const handleSort = (field?: string) => {
    if (!field) return;
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: field, direction });
  };

  const filtered = useMemo(() => {
    if (!query) return data;
    const q = query.toLowerCase();
    
    // Mapa de termos visíveis na UI para os valores em inglês do banco
    const aliasMap: Record<string, string[]> = {
      'entregue': ['delivered'],
      'pendente': ['pending'],
      'cancelado': ['canceled'],
      'ativo': ['order', 'active']
    };
    
    const searchTerms = [q];
    Object.entries(aliasMap).forEach(([ptTerm, internalTerms]) => {
      if (ptTerm.includes(q)) {
        searchTerms.push(...internalTerms);
      }
    });

    return data.filter((row) => {
      return Object.keys(row).some((k) => {
        const val = String(row[k]).toLowerCase();
        return searchTerms.some((term) => val.includes(term));
      });
    });
  }, [data, query]);

  const sorted = useMemo(() => {
    let sortableItems = [...filtered];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key] !== undefined && a[sortConfig.key] !== null ? String(a[sortConfig.key]).toLowerCase() : '';
        const valB = b[sortConfig.key] !== undefined && b[sortConfig.key] !== null ? String(b[sortConfig.key]).toLowerCase() : '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filtered, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

  const pageData = sorted.slice((page - 1) * pageSize, page * pageSize);


  const clearSelection = () => setSelected({});

  const handleDelete = () => {
    if (!onDelete) return;
    const selectedRows = Object.keys(selected).filter(k => selected[Number(k)]).map((k) => filtered[Number(k)]);
    onDelete(selectedRows);
    clearSelection();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          {selectable && onDelete && (
            <button className="px-3 py-1 bg-red-500 text-white rounded" onClick={handleDelete}>Excluir</button>
          )}
        </div>
        <div>
          <input
            placeholder="Pesquisar..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            className="border dark:border-gray-600 p-1 rounded dark:bg-[#33373E] dark:text-gray-200"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {columns.map((col, ci) => (
                <th key={ci} style={{ width: col.width }} 
                    onClick={() => handleSort(col.field)}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider select-none ${col.field ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700' : ''}`}>
                  <div className="flex items-center gap-1">
                    {col.headerText}
                    {sortConfig && sortConfig.key === col.field && (
                      <span className="text-gray-900 dark:text-gray-200 text-sm">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-secondary-dark-bg divide-y divide-gray-200 dark:divide-gray-700">
            {pageData.map((row, ri) => (
              <tr 
                key={ri} 
                onClick={() => onRowClick && onRowClick(row)}
                className={onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-[#3E434D] transition-colors' : ''}
              >
                {columns.map((col, ci) => {
                  const Cell = col.template;
                  return (
                    <td key={ci} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                          {Cell ? React.createElement(Cell as any, row) : (col.field ? row[col.field] : '')}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Exibindo {Math.min(filtered.length, (page - 1) * pageSize + 1)} a {Math.min(filtered.length, page * pageSize)} de {filtered.length}</p>
        </div>
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-2 py-1 border dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700">Anterior</button>
          <span className="px-2">{page} / {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-2 py-1 border dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700">Próximo</button>
        </div>
      </div>
    </div>
  );
};

export default SimpleTable;
